<template>
  <div>
    <!-- Project Details -->
    <div
      class="px-2 sm:px-4 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700"
    >
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2 sm:gap-4 text-xs sm:text-sm">
        <!-- Start date - editable -->
        <div>
          <span class="text-gray-500 dark:text-gray-400">Start:</span>
          <!-- View mode -->
          <span
            v-if="!isEditingStartDate"
            class="text-gray-900 dark:text-white ml-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 rounded px-1 inline-flex items-center gap-1"
            @click="startEditingStart"
            title="Klik om te bewerken"
          >
            {{ formatDate(prospect.start_date) }}
            <PencilIcon class="h-3 w-3 text-gray-400" />
          </span>
          <!-- Edit mode -->
          <div v-else class="mt-1 space-y-2">
            <DatePickerLite
              :model-value="editingStartDate"
              @update:model-value="editingStartDate = $event"
              placeholder="Selecteer datum"
            />
            <div class="flex gap-1">
              <button
                @click="saveStartDate"
                :disabled="isSaving"
                class="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {{ isSaving ? 'Opslaan...' : 'Opslaan' }}
              </button>
              <button
                @click="cancelStartEdit"
                :disabled="isSaving"
                class="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
        <!-- End date - editable -->
        <div>
          <span class="text-gray-500 dark:text-gray-400">Eind:</span>
          <!-- View mode -->
          <span
            v-if="!isEditingEndDate"
            class="text-gray-900 dark:text-white ml-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 rounded px-1 inline-flex items-center gap-1"
            @click="startEditingEnd"
            title="Klik om te bewerken"
          >
            {{ formatDate(prospect.expected_closing_date) }}
            <PencilIcon class="h-3 w-3 text-gray-400" />
          </span>
          <!-- Edit mode -->
          <div v-else class="mt-1 space-y-2">
            <DatePickerLite
              :model-value="editingEndDate"
              @update:model-value="editingEndDate = $event"
              placeholder="Selecteer datum"
            />
            <div class="flex gap-1">
              <button
                @click="saveEndDate"
                :disabled="isSaving"
                class="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {{ isSaving ? 'Opslaan...' : 'Opslaan' }}
              </button>
              <button
                @click="cancelEndEdit"
                :disabled="isSaving"
                class="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
        <div>
          <span class="text-gray-500 dark:text-gray-400">Verantwoordelijke:</span>
          <span class="text-gray-900 dark:text-white ml-1">{{
            prospect.responsible_employee_name || '-'
          }}</span>
        </div>
        <div>
          <span class="text-gray-500 dark:text-gray-400">Goedgekeurd door:</span>
          <span class="text-gray-900 dark:text-white ml-1">{{
            prospect.reviewed_by_name || 'Performance Lead'
          }}</span>
        </div>
        <!-- Kans - editable -->
        <div>
          <span class="text-gray-500 dark:text-gray-400">Kans:</span>
          <!-- View mode -->
          <span
            v-if="!isEditingChance"
            class="text-gray-900 dark:text-white ml-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 rounded px-1 inline-flex items-center gap-1"
            @click="startEditingChance"
            title="Klik om te bewerken"
          >
            {{ prospect.chance_to_score ?? '-' }}%
            <PencilIcon class="h-3 w-3 text-gray-400" />
          </span>
          <!-- Edit mode -->
          <div v-else class="mt-1 space-y-2">
            <input
              v-model.number="editingChance"
              type="number"
              min="1"
              max="100"
              step="1"
              class="w-20 text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              @keyup.enter="saveChance"
              @keyup.escape="cancelChanceEdit"
            />
            <div class="flex gap-1">
              <button
                @click="saveChance"
                :disabled="isSaving"
                class="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {{ isSaving ? 'Opslaan...' : 'Opslaan' }}
              </button>
              <button
                @click="cancelChanceEdit"
                :disabled="isSaving"
                class="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Error message -->
      <div v-if="errorMessage" class="mt-2 text-xs text-red-600 dark:text-red-400">
        {{ errorMessage }}
      </div>
      <!-- Sync status message -->
      <div v-if="syncStatus" class="mt-2 text-xs" :class="syncStatusClass">
        {{ syncStatus }}
      </div>
    </div>

    <!-- PL Review Feedback -->
    <div
      v-if="prospect.review_feedback"
      class="px-2 sm:px-4 py-2 sm:py-3 border-b border-gray-100 dark:border-gray-700"
      :class="
        prospect.workflow_status === 'te_bespreken' || prospect.workflow_status === 'afgekeurd'
          ? 'bg-orange-50 dark:bg-orange-900/20'
          : 'bg-blue-50 dark:bg-blue-900/20'
      "
    >
      <div class="flex items-start gap-2 text-sm">
        <ChatBubbleLeftEllipsisIcon
          class="h-5 w-5 flex-shrink-0 mt-0.5"
          :class="
            prospect.workflow_status === 'te_bespreken' || prospect.workflow_status === 'afgekeurd'
              ? 'text-orange-500 dark:text-orange-400'
              : 'text-blue-500 dark:text-blue-400'
          "
        />
        <div>
          <span
            class="font-medium"
            :class="
              prospect.workflow_status === 'te_bespreken' ||
              prospect.workflow_status === 'afgekeurd'
                ? 'text-orange-700 dark:text-orange-300'
                : 'text-blue-700 dark:text-blue-300'
            "
          >
            Feedback van {{ prospect.reviewed_by_name || 'Performance Lead' }}:
          </span>
          <span
            class="ml-1"
            :class="
              prospect.workflow_status === 'te_bespreken' ||
              prospect.workflow_status === 'afgekeurd'
                ? 'text-orange-600 dark:text-orange-400'
                : 'text-blue-600 dark:text-blue-400'
            "
          >
            {{ prospect.review_feedback }}
          </span>
        </div>
        <WorkflowFeedbackHistory :prospect-id="prospect.id" class="px-7" />
      </div>
    </div>

    <!-- Feedback history fallback (no current feedback but may have history) -->
    <div
      v-else-if="prospect.workflow_status !== 'nieuw'"
      class="px-2 sm:px-4 py-1 border-b border-gray-100 dark:border-gray-700"
    >
      <WorkflowFeedbackHistory :prospect-id="prospect.id" />
    </div>

    <!-- Employee Preferences / Roles -->
    <div class="p-2 sm:p-4">
      <div class="flex items-center justify-between mb-3 sm:mb-4">
        <h3
          class="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider"
        >
          Benodigde medewerkers
        </h3>
      </div>

      <!-- Loading preferences -->
      <div v-if="isLoadingPreferences" class="text-center py-4">
        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
      </div>

      <!-- No preferences -->
      <div
        v-else-if="preferences.length === 0"
        class="text-gray-500 dark:text-gray-400 text-sm py-4"
      >
        Geen medewerker voorkeuren gedefinieerd voor dit project.
      </div>

      <!-- Preferences list -->
      <div v-else class="space-y-4">
        <SourcingRoleCard
          v-for="(pref, prefIndex) in preferences"
          :key="pref.id || prefIndex"
          :prospect="prospect"
          :preference="pref"
          :pref-index="prefIndex"
          :assignment="getAssignmentForPreference(pref)"
          :selected-employee-id="getSelectedEmployeeId(pref, prefIndex)"
          :custom-selected-employee="getCustomSelectedEmployee(pref, prefIndex)"
          :match-stats="getMatchStatsForPref()"
          :employee-availability-data="getEmployeeAvailabilityData()"
          :employees-map="employeesMap"
          :teams-map="teamsMap"
          :is-assigning="getIsAssigning(pref, prefIndex)"
          :can-assign="getCanAssign(pref, prefIndex)"
          :expanded-graphs="getExpandedGraphs(pref, prefIndex)"
          :is-availability-expanded="getIsAvailabilityExpanded(pref, prefIndex)"
          :get-other-proposals-fn="getOtherProposalsFn"
          @select-employee="empId => handleSelectEmployee(pref, prefIndex, empId)"
          @assign="handleAssign(pref, prefIndex)"
          @remove-assignment="handleRemoveAssignment"
          @open-employee-selector="handleOpenEmployeeSelector(pref, prefIndex)"
          @clear-custom-employee="handleClearCustomEmployee(pref, prefIndex)"
          @toggle-availability="handleToggleAvailability(pref, prefIndex)"
          @toggle-employee-graph="empId => handleToggleEmployeeGraph(pref, prefIndex, empId)"
        />
      </div>

      <!-- All assigned indicator -->
      <div
        v-if="isFullyAssigned"
        class="mt-4 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg"
      >
        <div class="flex items-center gap-2 text-green-700 dark:text-green-400">
          <CheckCircleIcon class="h-5 w-5" />
          <span class="font-medium">Alle rollen zijn toegewezen</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { CheckCircleIcon, PencilIcon, ChatBubbleLeftEllipsisIcon } from '@heroicons/vue/24/outline'
import WorkflowFeedbackHistory from '@/components/common/feedback/WorkflowFeedbackHistory.vue'
import SourcingRoleCard from './SourcingRoleCard.vue'
import DatePickerLite from '@/components/common/inputs/DatePickerLite.vue'
import { prospectsLeadsService } from '@/services/prospectService.js'

const props = defineProps({
  prospect: {
    type: Object,
    required: true
  },
  preferences: {
    type: Array,
    default: () => []
  },
  assignments: {
    type: Array,
    default: () => []
  },
  isLoadingPreferences: {
    type: Boolean,
    default: false
  },
  isFullyAssigned: {
    type: Boolean,
    default: false
  },
  employeesMap: {
    type: Object,
    default: () => ({})
  },
  teamsMap: {
    type: Object,
    default: () => ({})
  },
  // Functions passed from parent
  getSelectedEmployeeIdFn: {
    type: Function,
    default: () => () => null
  },
  getCustomSelectedEmployeeFn: {
    type: Function,
    default: () => () => null
  },
  getIsAssigningFn: {
    type: Function,
    default: () => () => false
  },
  getCanAssignFn: {
    type: Function,
    default: () => () => false
  },
  getExpandedGraphsFn: {
    type: Function,
    default: () => () => ({})
  },
  getIsAvailabilityExpandedFn: {
    type: Function,
    default: () => () => true
  },
  getMatchStatsFn: {
    type: Function,
    default: () => () => ({})
  },
  getEmployeeAvailabilityDataFn: {
    type: Function,
    default: () => () => ({})
  },
  getOtherProposalsFn: {
    type: Function,
    default: () => () => []
  }
})

const emit = defineEmits([
  'select-employee',
  'assign',
  'remove-assignment',
  'open-employee-selector',
  'clear-custom-employee',
  'toggle-availability',
  'toggle-employee-graph',
  'prospect-updated'
])

// ==================== Date Editing State ====================
const isEditingStartDate = ref(false)
const isEditingEndDate = ref(false)
const isEditingChance = ref(false)
const editingStartDate = ref('')
const editingEndDate = ref('')
const editingChance = ref(null)
const isSaving = ref(false)
const errorMessage = ref('')
const syncStatus = ref('')

// Computed for sync status styling
const syncStatusClass = computed(() => {
  if (syncStatus.value.includes('mislukt') || syncStatus.value.includes('error')) {
    return 'text-orange-600 dark:text-orange-400'
  }
  if (syncStatus.value.includes('Lokaal')) {
    return 'text-gray-500 dark:text-gray-400'
  }
  return 'text-green-600 dark:text-green-400'
})

// Helper functions
function formatDate(dateString) {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

// Format date for editing (YYYY-MM-DD)
function toDateString(dateString) {
  if (!dateString) return ''
  return dateString.split('T')[0]
}

// ==================== Date Editing Functions ====================

// Start editing
function startEditingStart() {
  editingStartDate.value = toDateString(props.prospect.start_date)
  isEditingStartDate.value = true
  errorMessage.value = ''
}

function startEditingEnd() {
  editingEndDate.value = toDateString(props.prospect.expected_closing_date)
  isEditingEndDate.value = true
  errorMessage.value = ''
}

// Cancel editing
function cancelStartEdit() {
  isEditingStartDate.value = false
  editingStartDate.value = ''
  errorMessage.value = ''
}

function cancelEndEdit() {
  isEditingEndDate.value = false
  editingEndDate.value = ''
  errorMessage.value = ''
}

// Save handlers
async function saveStartDate() {
  if (!editingStartDate.value) {
    errorMessage.value = 'Selecteer een datum'
    return
  }

  // Validate: start date should not be after end date
  const endDate = props.prospect.expected_closing_date?.split('T')[0]
  if (endDate && editingStartDate.value > endDate) {
    errorMessage.value = 'Startdatum mag niet na einddatum liggen'
    return
  }

  await saveDate({ start_date: editingStartDate.value })
}

async function saveEndDate() {
  if (!editingEndDate.value) {
    errorMessage.value = 'Selecteer een datum'
    return
  }

  // Validate: end date should not be before start date
  const startDate = props.prospect.start_date?.split('T')[0]
  if (startDate && editingEndDate.value < startDate) {
    errorMessage.value = 'Einddatum mag niet voor startdatum liggen'
    return
  }

  await saveDate({ expected_closing_date: editingEndDate.value })
}

// Generic save date function
async function saveDate(updateData) {
  isSaving.value = true
  errorMessage.value = ''
  syncStatus.value = 'Opslaan...'

  try {
    const result = await prospectsLeadsService.updateDates(props.prospect.id, updateData)

    if (result.success) {
      // Emit event to update parent's prospect data
      emit('prospect-updated', result.data)

      // Show sync status
      if (result.simplicate_synced) {
        syncStatus.value = 'Gesynchroniseerd met Simplicate'
      } else if (result.simplicate_skipped) {
        syncStatus.value = 'Lokaal opgeslagen (Simplicate sync niet actief voor dit project)'
      } else if (result.simplicate_error) {
        syncStatus.value = `Lokaal opgeslagen, Simplicate sync mislukt: ${result.simplicate_error}`
      } else {
        syncStatus.value = 'Opgeslagen'
      }

      // Close editing
      isEditingStartDate.value = false
      isEditingEndDate.value = false
      editingStartDate.value = ''
      editingEndDate.value = ''

      // Clear status after 5 seconds
      setTimeout(() => {
        syncStatus.value = ''
      }, 5000)
    } else {
      errorMessage.value = result.error || 'Fout bij opslaan'
      syncStatus.value = ''
    }
  } catch (err) {
    console.error('Error updating dates:', err)
    errorMessage.value = err.response?.data?.error || 'Fout bij opslaan van datums'
    syncStatus.value = ''
  } finally {
    isSaving.value = false
  }
}

// ==================== Chance Editing Functions ====================

function startEditingChance() {
  editingChance.value = props.prospect.chance_to_score ?? null
  isEditingChance.value = true
  errorMessage.value = ''
}

function cancelChanceEdit() {
  isEditingChance.value = false
  editingChance.value = null
  errorMessage.value = ''
}

async function saveChance() {
  const val = editingChance.value
  if (val === null || val === undefined || val === '') {
    errorMessage.value = 'Vul een waarde in'
    return
  }
  if (!Number.isInteger(val) || val < 1 || val > 100) {
    errorMessage.value = 'Kans moet een geheel getal tussen 1 en 100 zijn'
    return
  }

  isSaving.value = true
  errorMessage.value = ''
  syncStatus.value = 'Opslaan...'

  try {
    const result = await prospectsLeadsService.updateChanceToScore(props.prospect.id, val)

    if (result.success) {
      emit('prospect-updated', result.data)

      if (result.simplicate_synced) {
        syncStatus.value = 'Gesynchroniseerd met Simplicate'
      } else if (result.simplicate_skipped) {
        syncStatus.value = 'Lokaal opgeslagen (Simplicate sync niet actief voor dit project)'
      } else if (result.simplicate_error) {
        syncStatus.value = `Lokaal opgeslagen, Simplicate sync mislukt: ${result.simplicate_error}`
      } else {
        syncStatus.value = 'Opgeslagen'
      }

      isEditingChance.value = false
      editingChance.value = null

      setTimeout(() => {
        syncStatus.value = ''
      }, 5000)
    } else {
      errorMessage.value = result.error || 'Fout bij opslaan'
      syncStatus.value = ''
    }
  } catch (err) {
    console.error('Error updating chance_to_score:', err)
    errorMessage.value = err.response?.data?.error || 'Fout bij opslaan van scoringskans'
    syncStatus.value = ''
  } finally {
    isSaving.value = false
  }
}

function getAssignmentForPreference(pref) {
  const assignments = props.assignments.filter(a => a.is_active !== false)

  // Primary: match via notes marker (used by new assignments)
  if (pref.id) {
    const byNotes = assignments.find(a => a.notes && a.notes.includes(`pref:${pref.id}`))
    if (byNotes) return byNotes
  }

  // Assignment met een pref-marker voor een ándere rol telt niet als fallback
  const belongsToOtherPref = a => {
    const m = a.notes?.match(/pref:([^\s|]+)/)
    return !!(m && pref.id !== undefined && String(m[1]) !== String(pref.id))
  }

  // Fallback 1: single preference → any legacy assignment matches
  if (
    props.preferences.length === 1 &&
    assignments.length > 0 &&
    !belongsToOtherPref(assignments[0])
  ) {
    return assignments[0]
  }

  // Fallback 2: match via PL-selected employee (alleen legacy assignments)
  if (pref.pl_selected_employee_id) {
    const byPl = assignments.find(
      a => a.employee_simplicate_id === pref.pl_selected_employee_id && !belongsToOtherPref(a)
    )
    if (byPl) return byPl
  }

  return undefined
}

function getSelectedEmployeeId(pref, prefIndex) {
  return props.getSelectedEmployeeIdFn(pref, prefIndex)
}

function getCustomSelectedEmployee(pref, prefIndex) {
  return props.getCustomSelectedEmployeeFn(pref, prefIndex)
}

function getIsAssigning(pref, prefIndex) {
  return props.getIsAssigningFn(pref, prefIndex)
}

function getCanAssign(pref, prefIndex) {
  return props.getCanAssignFn(pref, prefIndex)
}

function getExpandedGraphs(pref, prefIndex) {
  return props.getExpandedGraphsFn(pref, prefIndex)
}

function getIsAvailabilityExpanded(pref, prefIndex) {
  return props.getIsAvailabilityExpandedFn(pref, prefIndex)
}

function getMatchStatsForPref() {
  return props.getMatchStatsFn()
}

function getEmployeeAvailabilityData() {
  return props.getEmployeeAvailabilityDataFn()
}

// Event handlers
function handleSelectEmployee(pref, prefIndex, empId) {
  emit('select-employee', { pref, prefIndex, empId })
}

function handleAssign(pref, prefIndex) {
  emit('assign', { pref, prefIndex })
}

function handleRemoveAssignment(assignment) {
  emit('remove-assignment', assignment)
}

function handleOpenEmployeeSelector(pref, prefIndex) {
  emit('open-employee-selector', { pref, prefIndex })
}

function handleClearCustomEmployee(pref, prefIndex) {
  emit('clear-custom-employee', { pref, prefIndex })
}

function handleToggleAvailability(pref, prefIndex) {
  emit('toggle-availability', { pref, prefIndex })
}

function handleToggleEmployeeGraph(pref, prefIndex, empId) {
  emit('toggle-employee-graph', { pref, prefIndex, empId })
}
</script>
